/**
 * KartYazdirModal.jsx — Kişi Listesi'nde "Kart Yazdır" penceresi (backlog #128, #278).
 *
 * Açılınca `GET /personnel/{personId}/kart-karari?fuarId=` (backend #277) okunur: kişinin o fuardaki HER
 * firması için kararı, kartı engelleyen maddeler, vergi numarası ve 24 saat uyarısı. Her engel, düzeltileceği
 * yere YENİ SEKMEDE açılan bir bağlantıdır; başka sekmede yapılan iş bu pencereye kendiliğinden yansımaz,
 * o yüzden "Yenile" var. Düğme hem "Tüm Kişiler"de hem fuar görünümünde açılır (kırmızı/sarı satırda da):
 * eskiden fuar görünümünde karar yeşil değilse düğme kapalıydı, neden kapalı olduğu görünmüyordu.
 *
 * `fuarId` HER ZAMAN üst çubuktaki fuardır (sayfadaki ayrı bir fuar seçimi değil; bkz. TumPersonellerPage).
 *
 * `POST /personnel/{personId}/kart-yazdir?firmaId=&fuarId=` kartı yalnızca BASILDI diye damgalar
 * (card_counter + printed_at) ve erişim penceresini döner; basılacak bir kart yerleşimi ne backend'de
 * ne frontend'de var. Bu yüzden akış: karar → onay → damga → dönen geçerlilik.
 *
 * ⚠️ İdempotent DEĞİL: her çağrı sayacı 1 artırır ve `printed_at`i sıfırlar (TEMPORARY_24H'te 24 saatlik
 * saati yeniden başlatır). Bu yüzden tek tık basılmaz — yazdırma sürerken ve başarıdan sonra düğme kilitli.
 * Basılabilirlik kararı backend'in: `kartBasilabilir` tam olarak POST'un kabul ettiği şart (yeşil karar +
 * vergi numarası); POST artık yeşil olmayan kararı 400 ile de reddediyor.
 *
 * Firma seçimi YOK (#400): kişinin fuardaki firmalarının kararları tek karara indirilir (kartBirlestir.js) ve kart en
 * uygun firma üzerinden basılır — basılabilir olan, sonra kişisel engeli en az olan, eşitlikte satırın en yeşil firması
 * (`baslangicFirmaId`); listede firma süzgeci açıksa (`firmaSabitId`) yalnızca o firma. Engeller firmaya bağlanmaz
 * ("SGK: Eksik" bir kez yazılır); firma evrakı yalnızca HİÇBİR firmada tam değilse uyarılır ve "Firma Profili"
 * bağlantısı hangi firmanın profiline gidileceğini sorar. Basılacak firma künye şeridinde görünür.
 *
 * #350 — Üstte Kimlik Doğrula penceresiyle ortak künye şeridi (KisiKunyeSeridi): vesikalık, ad, T.C., telefon, kimlik durumu,
 * seçili firma, fuar logosu. Vesikalık, `fotografYukleme` ve `logo` kart-karari'nin üst düzeyinde gelir (backend #351; kişi
 * bu fuarda görevli olmasa da); satırdaki değer yedektir. Şeritteki "Kimlik Doğrula / Foto Ekle" Kimlik penceresini bu
 * pencerenin ÜSTÜNE açar (yığılmış modal; kapanınca Kart Yazdır açık kalır). Karar ya da fotoğraf değişince kart kararı
 * yeniden okunur ve `onKimlikSaved` / `onFotoDegisti` çağıranın satırını tazelemesi için iletilir.
 * #353 — Engel bağlantıları yeni sekmede açılır; `geri` bu pencerenin açıldığı liste adresidir (süzgeç/sayfa dahil), kişi
 * sayfasındaki "Kişi Listesine Dön" tam oraya götürür.
 *
 * #467 (backend #396) — "Kartta yazacak meslek": Çalışan (varsayılan) ya da kişinin tuttuğu, bu firma girdisinde basılabilir bir meslek.
 * Seçenekler `secili.kartMeslekleri[]` + `kartCalisanBasilabilir`den gelir (kural backend'de); basılamayanlar nedeniyle birlikte kapalı
 * listelenir. POST'a HER ZAMAN açık `meslekId` gider (Çalışan için 0): ekranda seçilen ile basılan aynı olsun. Kişinin hiç mesleği yoksa
 * seçecek bir şey yoktur, `meslekId` gönderilmez (backend Çalışan basar). Basılınca "Kartta" satırı backend'in `data.kartBasim`ını yazar
 * (kategori büyük, meslek küçük), altta açılır "Basım geçmişi" (`kart-basimlari`, bu fuar) bulunur. Firma seçimi hâlâ YOK (#400).
 *
 * Props: person (liste satırı), fuarId, fuarAdi, fuarLogoUrl (üst çubuk fuarı), baslangicFirmaId, firmaSabitId, onClose,
 * onFuaraEsle, onKimlikSaved(personId, data), onFotoDegisti(personId).
 */
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Alert, Spinner, Input, Label } from "reactstrap";
import { Printer, RefreshCw, Link2, ChevronDown, ChevronRight } from "lucide-react";
import { printCard, getKartKarari, getKartBasimlari, cardAccessModeMeta } from "../../services/cardAccessService";
import { kartKararMeta, isverenYolu, ISVEREN_ETIKETI } from "../../services/kartDurumuService";
import { statusBackground } from "../Projects/StandPills";
import { firmaAdi } from "./PersonelFirmaBelgeParcalari";
import KartUygunsuzluklari, { kartUyarilari, uygunsuzlukLabel, meslekEksikAdi } from "./KartUygunsuzluklari";
import { kartMeslekSecenekleri, secilenKartSecenegi, kartBasimYazisi } from "./meslekler";
import KisiKunyeSeridi from "./KisiKunyeSeridi";
import KimlikDogrulamaModal from "./KimlikDogrulamaModal";
import { kartKararBirlestir } from "./kartBirlestir";
import { useTranslation } from "react-i18next";
import { currentLocaleTag } from "../../i18n";
import i18n from "../../i18n";

/** Erişim penceresinin sonu: LocalDateTime ("2026-10-02T14:30:00") ya da tarih ("2026-10-02"). */
function gecerlilikYaz(deger) {
  if (!deger) return null;
  const s = String(deger);
  if (s.includes("T")) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? s : d.toLocaleString(currentLocaleTag(), { dateStyle: "short", timeStyle: "short" });
  }
  const [y, m, g] = s.split("-").map(Number);
  return y && m && g ? new Date(y, m - 1, g).toLocaleDateString(currentLocaleTag(), { dateStyle: "short" }) : s;
}

/** `kart-basimlari`ndaki tarih ("2026-10-19 14:30:00" ya da ISO) yerel kısa tarih-saat olarak; okunamazsa olduğu gibi. */
function basimTarihiYaz(deger) {
  if (!deger) return "";
  const d = new Date(String(deger).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(deger) : d.toLocaleString(currentLocaleTag(), { dateStyle: "short", timeStyle: "short" });
}

/** Kartta yazan: Çalışan, ya da kategori (kalın) + meslek (küçük). `kartBasimYazisi` kartBasim / kart-basimlari satırını okur. */
function KartYazisi({ kartBasim }) {
  const { t } = useTranslation("components");
  const yazi = kartBasimYazisi(kartBasim);
  if (yazi.calisan) return <>{t("kartYazdirModal.calisan")}</>;
  return (
    <>
      <strong>{yazi.ana}</strong>
      {yazi.ince && <small className="ms-1">{yazi.ince}</small>}
    </>
  );
}

function hataMesaji(e, varsayilan) {
  const veri = e?.response?.data;
  return (typeof veri === "string" ? veri : veri?.message) || e?.message || varsayilan;
}

/** `onFuaraEsle` (#261): kişi bu fuarda görevli değilse "Fuara Eşle" düğmesi çıkar; sayfa bu pencereyi kapatıp eşleme popup'ını açar. */
export default function KartYazdirModal({
  person, fuarId, fuarAdi, fuarLogoUrl = null, baslangicFirmaId, firmaSabitId = null, onClose, onFuaraEsle = null,
  onKimlikSaved = null, onFotoDegisti = null,
}) {
  const { t: tx } = useTranslation("components");
  const location = useLocation();
  // #353: engel bağlantıları yeni sekmede açılır; "geri" bu listenin tam adresi (süzgeç / sayfa adreste durur).
  const geri = location.pathname + location.search;
  const [veri, setVeri] = useState(null);
  const [okunuyor, setOkunuyor] = useState(true);
  const [okumaHatasi, setOkumaHatasi] = useState(null);
  const [yenileToken, setYenileToken] = useState(0);
  const [basiliyor, setBasiliyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [sonuc, setSonuc] = useState(null);
  // #350: Kimlik Doğrula / Foto Ekle üstte yığılır. Karar yazılınca kart kararı yeniden okunur, ama kimlik durumu o okuma
  // gelene dek (ve kişi bu fuarda görevli değilse hiç) satırdan eskimiş kalmasın diye yazılan değer burada tutulur.
  const [kimlikAcik, setKimlikAcik] = useState(false);
  const [kimlikDurumYeni, setKimlikDurumYeni] = useState(null);
  const [fotoSurum, setFotoSurum] = useState(0);
  // #467: kullanıcının seçtiği değer (kartMeslekSecenekleri.deger); null = varsayılan. Geçerli seçim türetilir (secilenKartSecenegi),
  // böylece yenilemeden sonra seçenek kaybolur ya da basılamaz olursa kendiliğinden varsayılana döner.
  const [meslekSecimi, setMeslekSecimi] = useState(null);
  // "Basım geçmişi": bu fuardaki basımlar; basımdan sonra yeniden okunur. Hata ikincil bir bilgiyi susturur (liste boş kalır).
  const [gecmis, setGecmis] = useState([]);
  const [gecmisAcik, setGecmisAcik] = useState(false);
  const [gecmisToken, setGecmisToken] = useState(0);

  useEffect(() => {
    let ignore = false;
    async function oku() {
      try {
        const liste = await getKartBasimlari(person.personId, fuarId);
        if (!ignore) setGecmis(liste);
      } catch {
        if (!ignore) setGecmis([]);
      }
    }
    oku();
    return () => { ignore = true; };
  }, [person.personId, fuarId, gecmisToken]);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect) — başlangıç
  // setState'leri doğrudan efektin gövdesinde olunca derleme hatası veriyor.
  useEffect(() => {
    let ignore = false;
    async function oku() {
      setOkunuyor(true);
      setOkumaHatasi(null);
      try {
        const d = await getKartKarari(person.personId, fuarId);
        if (ignore) return;
        setVeri(d);
      } catch (e) {
        if (!ignore) setOkumaHatasi(hataMesaji(e, i18n.t("components:kartYazdirModal.kartKarariOkunamadi")));
      } finally {
        if (!ignore) setOkunuyor(false);
      }
    }
    oku();
    return () => { ignore = true; };
  }, [person.personId, fuarId, yenileToken]);

  // #400: firma başına kararlar tek karara iner; kart `secili` firma üzerinden basılır.
  const birlesik = kartKararBirlestir(veri?.firmalar, { tercihFirmaId: baslangicFirmaId, sabitFirmaId: firmaSabitId });
  const secili = birlesik?.secili ?? null;
  const firmaId = secili?.firmaId ?? null;
  const karar = kartKararMeta(secili);
  // #248: kararın geldiği yol da aynı girdiden okunur (her firma kendi kartYolu'nu taşır).
  const isverenOlarak = isverenYolu(secili);
  const gecici24 = secili?.kartErisim?.cardAccessMode === "TEMPORARY_24H";
  const uyarilar = kartUyarilari(secili?.kisi);
  const mesgul = basiliyor || okunuyor;
  // #467: kartta yazacak meslek. Seçenek yoksa (kişinin mesleği yok) seçim de yok: meslekId gönderilmez.
  // `kartMeslekleri` + `kartCalisanBasilabilir` girdinin kendisinde gelir (backend #396); eski yanıtta yalnızca `kisi`de.
  const meslekSecenekleri = kartMeslekSecenekleri(secili?.kartMeslekleri ? secili : (secili?.kisi ?? null));
  const meslekSecenegi = secilenKartSecenegi(meslekSecenekleri, meslekSecimi);
  const meslekSecimiGecerli = meslekSecenekleri.length === 0 || Boolean(meslekSecenegi?.basilabilir);

  // Şerit verisi: kart-karari'nin üst düzeyi (kişi fuarda görevli olmasa da gelir) → satır. Kimlik: yeni yazılan → firmanın
  // okunan kişisi → satır.
  const fotografVar = Boolean(veri?.fotografVar ?? person.fotografVar) || fotoSurum > 0;
  const fotografYukleme = veri?.fotografYukleme ?? person.fotografYukleme ?? null;
  const kimlikDurum = kimlikDurumYeni
    ?? secili?.kisi?.kimlikDogrulama?.durum
    ?? person.kimlikDogrulama?.durum
    ?? 0;
  const seridiFirma = veri?.fuardaGorevli && secili ? firmaAdi(secili) : null;

  function kimlikKaydedildi(personId, res) {
    setKimlikAcik(false);
    if (res?.durum != null) setKimlikDurumYeni(Number(res.durum));
    setYenileToken((t) => t + 1);
    onKimlikSaved?.(personId, res);
  }

  function fotoDegisti(personId) {
    setFotoSurum((n) => n + 1);
    setYenileToken((t) => t + 1);
    onFotoDegisti?.(personId);
  }

  async function yazdir() {
    setBasiliyor(true);
    setHata(null);
    try {
      // WAS (until #467): await printCard(person.personId, firmaId, fuarId) — the backend chose what the card says.
      const res = await printCard(person.personId, firmaId, fuarId, meslekSecenegi ? meslekSecenegi.deger : null);
      if (res?.result === false) throw new Error(res.message || tx("kartYazdirModal.kartYazdirilamadi"));
      setSonuc(res?.data || {});
      setGecmisToken((n) => n + 1);
    } catch (e) {
      setHata(hataMesaji(e, tx("kartYazdirModal.kartYazdirilamadi")));
    } finally {
      setBasiliyor(false);
    }
  }

  const mod = sonuc ? cardAccessModeMeta(sonuc.cardAccessMode) : null;
  const gecerlilik = sonuc ? gecerlilikYaz(sonuc.accessValidUntil) : null;

  /** Seçici satırı: "Elektrik Ustası — Elektrik Sorumlusu"; basılamıyorsa nedeniyle ("Kaynakçı (Mesleki Eğitim eksik)"). */
  function secenekEtiketi(s) {
    const ad = s.calisan
      ? tx("kartYazdirModal.calisan")
      : (s.kategoriAdi && s.kategoriAdi !== s.ad ? `${s.ad} — ${s.kategoriAdi}` : s.ad);
    if (s.basilabilir) return ad;
    // Çalışan'ın eksikleri kart-karari'nde ayrı listelenmez (yalnızca kartCalisanBasilabilir): genel neden.
    if (s.calisan) return tx("kartYazdirModal.secenekCalisanBelgeEksik", { ad });
    const eksik = [...new Set(s.eksikler.map(meslekEksikAdi))].join(", ");
    return eksik ? tx("kartYazdirModal.secenekEksik", { ad, eksik }) : tx("kartYazdirModal.secenekBasilamaz", { ad });
  }

  return (
    <>
    <Modal isOpen centered size="lg" toggle={basiliyor ? undefined : onClose}>
      <ModalHeader toggle={basiliyor ? undefined : onClose}>{tx("kartYazdirModal.kartYazdir")}</ModalHeader>
      <ModalBody>
        <KisiKunyeSeridi
          person={person}
          kimlikDurum={kimlikDurum}
          fotografVar={fotografVar}
          fotoSurum={fotoSurum}
          fotografYukleme={fotografYukleme}
          firmaAdi={seridiFirma}
          firmaEki={seridiFirma ? birlesik.digerFirmaSayisi : 0}
          fuarAdi={fuarAdi || tx("kartYazdirModal.fuar", { fuarId })}
          fuarLogoUrl={fuarLogoUrl}
          onKimlik={() => setKimlikAcik(true)}
        />

        {okunuyor && !veri && (
          <div className="text-center py-3"><Spinner color="primary" size="sm" /></div>
        )}
        {okumaHatasi && <Alert fade={false} color="danger">{okumaHatasi}</Alert>}

        {veri && !veri.fuardaGorevli && (
          <Alert fade={false} color="secondary" className="mb-0">
            {tx("kartYazdirModal.kisiBuFuardaGorevlendirilmemisKart")}
            {veri.forbidden && <div className="mt-1">{uygunsuzlukLabel("YASAKLI")}</div>}
          </Alert>
        )}

        {veri?.fuardaGorevli && (
          <div style={okunuyor ? { opacity: 0.5 } : undefined}>
            {secili && (
              <>
                <div>
                  <span className="badge text-white fw-normal" style={{ background: statusBackground(karar.color) }}>
                    {karar.label}
                  </span>
                  {isverenOlarak && <span className="text-muted small ms-2">{ISVEREN_ETIKETI}</span>}
                </div>

                {!sonuc && (
                  <div className="mt-3">
                    <KartUygunsuzluklari
                      uygunsuzluklar={birlesik.uygunsuzluklar} vergiNoYok={birlesik.vergiNoYok}
                      firmaSecenekleri={birlesik.firmaSecenekleri}
                      // FIRMA grubu `kisi.firmaBelgeGruplari`nda gelir (kart-karari girdisinin kendisinde `belgeGruplari` yok)
                      firmaGrubu={(secili.kisi?.firmaBelgeGruplari || secili.belgeGruplari || []).find((g) => g.kod === "FIRMA") ?? null}
                      firmaId={secili.firmaId} personId={person.personId} fuarId={fuarId} yeniSekme
                      kisi={secili.kisi} geri={geri} geriEtiket={tx("kartYazdirModal.kisiListesineDon")}
                    />
                  </div>
                )}

                {gecici24 && !sonuc && (
                  <Alert fade={false} color="warning" className="mt-3 mb-0 py-2 small">
                    <strong>{cardAccessModeMeta("TEMPORARY_24H").label}.</strong>{" "}
                    {secili.eksikFuarBelgeleri?.length > 0
                      ? <>{tx("kartYazdirModal.fuarinIstedigiFirmaEvraklariEksik")}{secili.eksikFuarBelgeleri.map((b) => b.fileTypeName).join(", ")}): </>
                      : null}
                    {tx("kartYazdirModal.kartBasilirAmaBasimdanItibaren")}
                  </Alert>
                )}
                {uyarilar.map((u) => (
                  <Alert key={u} fade={false} color="warning" className="mt-2 mb-0 py-2 small">{u}</Alert>
                ))}

                {/* #467: kartta yazacak meslek. Kart hiç basılamıyorsa seçenek anlamsız, kişinin mesleği yoksa seçilecek bir şey yok. */}
                {!sonuc && secili.kartBasilabilir && meslekSecenekleri.length > 0 && (
                  <div className="mt-3">
                    <Label for="kart-meslek-secimi" className="form-label mb-1">{tx("kartYazdirModal.karttaYazacakMeslek")}</Label>
                    <Input
                      id="kart-meslek-secimi" type="select" bsSize="sm" disabled={mesgul}
                      value={meslekSecenegi?.deger ?? 0}
                      onChange={(e) => setMeslekSecimi(Number(e.target.value))}
                    >
                      {meslekSecenekleri.map((s) => (
                        <option key={s.deger} value={s.deger} disabled={!s.basilabilir}>{secenekEtiketi(s)}</option>
                      ))}
                    </Input>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {sonuc && (
          <Alert fade={false} color="success" className="mt-3 mb-0">
            {tx("kartYazdirModal.kartYazdirildi")}
            {gecerlilik && <>{" "}{tx("kartYazdirModal.gecerlilik")}{" "}<strong>{gecerlilik}</strong>.</>}
            {mod && sonuc.cardAccessMode !== "FULL" && <> {mod.label}.</>}
            {sonuc.kartBasim && (
              <div className="mt-1">
                {tx("kartYazdirModal.kartta")} <KartYazisi kartBasim={sonuc.kartBasim} />
                {(sonuc.kartBasim.firmaShortname || sonuc.kartBasim.firmaName) && <> · {sonuc.kartBasim.firmaShortname || sonuc.kartBasim.firmaName}</>}
              </div>
            )}
          </Alert>
        )}
        {hata && <Alert fade={false} color="danger" className="mt-3 mb-0">{hata}</Alert>}

        {gecmis.length > 0 && (
          <div className="mt-3">
            <Button color="link" size="sm" className="p-0 text-decoration-none" onClick={() => setGecmisAcik((a) => !a)}>
              {gecmisAcik ? <ChevronDown size={14} className="me-1" /> : <ChevronRight size={14} className="me-1" />}
              {tx("kartYazdirModal.basimGecmisi", { sayi: gecmis.length })}
            </Button>
            {gecmisAcik && (
              <ul className="list-unstyled small text-muted mb-0 mt-1">
                {gecmis.map((g) => (
                  <li key={g.id}>
                    {basimTarihiYaz(g.basimTarihi)} · {g.firmaShortname || g.firmaName} · <KartYazisi kartBasim={g} />
                    {g.basanUserName && <> · {g.basanUserName}</>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        {!sonuc && (
          <Button color="light" className="me-auto" onClick={() => setYenileToken((t) => t + 1)} disabled={mesgul}>
            {okunuyor ? <Spinner size="sm" className="me-1" /> : <RefreshCw size={14} className="me-1" />}
            {tx("common:yenile")}
          </Button>
        )}
        <Button color="light" onClick={onClose} disabled={basiliyor}>{sonuc ? tx("common:kapat") : tx("kartYazdirModal.vazgec")}</Button>
        {onFuaraEsle && veri && !veri.fuardaGorevli && !veri.forbidden && (
          <Button color="green" onClick={onFuaraEsle}>
            <Link2 size={14} className="me-1" />{tx("kartYazdirModal.fuaraEsle")}
          </Button>
        )}
        {!sonuc && (
          <Button color="primary" onClick={yazdir} disabled={!secili?.kartBasilabilir || mesgul || !firmaId || !meslekSecimiGecerli}>
            {basiliyor ? <Spinner size="sm" className="me-1" /> : <Printer size={14} className="me-1" />}
            {tx("kartYazdirModal.kartiYazdir")}
          </Button>
        )}
      </ModalFooter>
    </Modal>

    {/* Kimlik Doğrula / Foto Ekle, Kart Yazdır'ın ÜSTÜNDE (PersonelEkleModal'daki gibi yığılmış); kapanınca bu pencere açık kalır. */}
    {kimlikAcik && (
      <KimlikDogrulamaModal
        person={{ ...person, fotografVar, fotografYukleme, kimlikDogrulama: { ...person.kimlikDogrulama, durum: kimlikDurum } }}
        fuarAdi={fuarAdi || tx("kartYazdirModal.fuar", { fuarId })}
        fuarLogoUrl={fuarLogoUrl}
        firmaFiltreId={firmaId}
        onClose={() => setKimlikAcik(false)}
        onSaved={kimlikKaydedildi}
        onFotoDegisti={fotoDegisti}
      />
    )}
    </>
  );
}
